import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaSpinner, FaCheckCircle, FaExclamationTriangle, FaChartLine } from 'react-icons/fa';
import api from '../api';

const OrderTicketModal = ({ isOpen, onClose, stockSymbol = '', onSuccess }) => {
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [formData, setFormData] = useState({
    stock_symbol: stockSymbol,
    broker: 'ZERODHA',
    exchange: 'NSE',
    quantity: 1,
    buy_trigger_price: '',
    buy_limit_price: '',
    sell_target_trigger_price: '',
    sell_target_limit_price: '',
    sell_sl_trigger_price: '',
    sell_sl_limit_price: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [triggerIds, setTriggerIds] = useState({ buy: null, sell: null });
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchBrokers();
      setFormData(prev => ({ ...prev, stock_symbol: stockSymbol }));
    } else {
      // Reset on close
      setError('');
      setSuccess(false);
      setTriggerIds({ buy: null, sell: null });
      setHasAcknowledged(false);
    }
  }, [isOpen, stockSymbol]);

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/v1/brokers/');
      const activeBrokers = response.data.filter(b => b.is_active);
      setConnectedBrokers(activeBrokers);
      
      if (activeBrokers.length === 0) {
        setError('No active broker connections found. Please connect a broker first.');
      } else if (activeBrokers.length > 0) {
        setFormData(prev => ({ ...prev, broker: activeBrokers[0].broker }));
      }
    } catch (err) {
      setError('Failed to fetch broker connections');
    }
  };

  // Calculate risk/reward metrics for GTT order
  const metrics = useMemo(() => {
    const buyLimitPrice = parseFloat(formData.buy_limit_price) || 0;
    const targetTriggerPrice = parseFloat(formData.sell_target_trigger_price) || 0;
    const targetLimitPrice = parseFloat(formData.sell_target_limit_price) || 0;
    const slTriggerPrice = parseFloat(formData.sell_sl_trigger_price) || 0;
    const slLimitPrice = parseFloat(formData.sell_sl_limit_price) || 0;
    const buyTriggerPrice = parseFloat(formData.buy_trigger_price) || 0;
    const quantity = parseInt(formData.quantity) || 0;

    const potentialProfit = (targetLimitPrice - buyLimitPrice) * quantity;
    const potentialLoss = (buyLimitPrice - slLimitPrice) * quantity;
    const riskRewardRatio = potentialLoss > 0 ? potentialProfit / potentialLoss : 0;
    
    const profitPercent = buyLimitPrice > 0 ? ((targetLimitPrice - buyLimitPrice) / buyLimitPrice) * 100 : 0;
    const lossPercent = buyLimitPrice > 0 ? ((buyLimitPrice - slLimitPrice) / buyLimitPrice) * 100 : 0;

    // GTT-specific validation
    const isValid = 
      buyLimitPrice > 0 &&
      buyTriggerPrice > 0 &&
      buyLimitPrice >= buyTriggerPrice &&
      targetTriggerPrice > buyLimitPrice &&
      targetLimitPrice > 0 &&
      targetLimitPrice <= targetTriggerPrice &&
      slTriggerPrice > 0 &&
      slTriggerPrice < buyLimitPrice &&
      slLimitPrice > 0 &&
      slLimitPrice <= slTriggerPrice;

    return {
      potentialProfit,
      potentialLoss,
      riskRewardRatio,
      profitPercent,
      lossPercent,
      isValid
    };
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.stock_symbol.trim()) {
      setError('Please enter a stock symbol');
      return;
    }

    if (!metrics.isValid) {
      setError('Invalid prices. Please check GTT price requirements in the Important Notes section.');
      return;
    }

    if (connectedBrokers.length === 0) {
      setError('No broker connected. Please connect a broker first.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/v1/trading/place-gtt-order', formData);
      
      if (response.data.success) {
        setSuccess(true);
        setTriggerIds({
          buy: response.data.buy_trigger_id,
          sell: response.data.sell_oco_trigger_id
        });
        
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Auto-close after 3 seconds
        setTimeout(() => {
          onClose();
          setSuccess(false);
          // Reset form
          setFormData({
            stock_symbol: '',
            broker: 'ZERODHA',
            exchange: 'NSE',
            quantity: 1,
            buy_trigger_price: '',
            buy_limit_price: '',
            sell_target_trigger_price: '',
            sell_target_limit_price: '',
            sell_sl_trigger_price: '',
            sell_sl_limit_price: ''
          });
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to place GTT order';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4 flex justify-between items-center z-10">
            <div>
              <div className="flex items-center">
                <FaChartLine className="mr-2 text-white" />
                <h2 className="text-2xl font-bold text-white">Place GTT Order</h2>
              </div>
              <p className="text-sm text-green-100 mt-1">Good Till Triggered - Entry, Target & Stop Loss</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FaTimes size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              // Success Message
              <div className="text-center py-8">
                <FaCheckCircle className="mx-auto text-green-500 text-6xl mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">GTT Orders Placed Successfully!</h3>
                <p className="text-gray-600 mb-4">Your GTT orders have been submitted to Zerodha</p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 inline-block">
                  <p className="text-sm text-green-800">
                    <strong>Buy GTT ID:</strong> {triggerIds.buy}<br />
                    <strong>Sell OCO GTT ID:</strong> {triggerIds.sell}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-4">This window will close automatically...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <FaExclamationTriangle className="text-red-500 mt-1 mr-3 flex-shrink-0" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* Section 1: Main Order Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Main Order Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Stock Symbol */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stock Symbol <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="stock_symbol"
                        value={formData.stock_symbol}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., RELIANCE"
                        required
                      />
                    </div>

                    {/* Broker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Broker <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="broker"
                        value={formData.broker}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={connectedBrokers.length === 0}
                        required
                      >
                        {connectedBrokers.map(broker => (
                          <option key={broker.id} value={broker.broker}>
                            {broker.broker}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Exchange */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exchange <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="exchange"
                        value={formData.exchange}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="NSE">NSE</option>
                        <option value="BSE">BSE</option>
                      </select>
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Buy Trigger */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">🔹 Buy Trigger (Entry Point)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Buy Trigger Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Trigger Price (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="buy_trigger_price"
                        value={formData.buy_trigger_price}
                        onChange={handleInputChange}
                        step="0.05"
                        min="0"
                        className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 2500.00"
                        required
                      />
                      <p className="text-xs text-gray-600 mt-1">Price at which buy order triggers</p>
                    </div>

                    {/* Buy Limit Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Limit Price (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="buy_limit_price"
                        value={formData.buy_limit_price}
                        onChange={handleInputChange}
                        step="0.05"
                        min="0"
                        className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 2505.00"
                        required
                      />
                      <p className="text-xs text-gray-600 mt-1">Maximum price willing to pay</p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Sell OCO (Target & Stop-Loss) */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">🔹 Sell OCO (Exit Strategy)</h3>
                  
                  {/* Target */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-green-700 mb-3">🎯 Target (Profit Exit)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trigger Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="sell_target_trigger_price"
                          value={formData.sell_target_trigger_price}
                          onChange={handleInputChange}
                          step="0.05"
                          min="0"
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="e.g., 2600.00"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Price at which target sell triggers</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Limit Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="sell_target_limit_price"
                          value={formData.sell_target_limit_price}
                          onChange={handleInputChange}
                          step="0.05"
                          min="0"
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="e.g., 2595.00"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Minimum price willing to sell</p>
                      </div>
                    </div>
                  </div>

                  {/* Stop-Loss */}
                  <div>
                    <h4 className="text-md font-medium text-red-700 mb-3">🛑 Stop-Loss (Risk Exit)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trigger Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="sell_sl_trigger_price"
                          value={formData.sell_sl_trigger_price}
                          onChange={handleInputChange}
                          step="0.05"
                          min="0"
                          className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="e.g., 2450.00"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Price at which stop-loss triggers</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Limit Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="sell_sl_limit_price"
                          value={formData.sell_sl_limit_price}
                          onChange={handleInputChange}
                          step="0.05"
                          min="0"
                          className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="e.g., 2445.00"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Minimum acceptable stop-loss price</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk/Reward Metrics */}
                {metrics.isValid && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-3">📊 Risk/Reward Analysis</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Potential Profit</p>
                        <p className="text-lg font-bold text-green-600">₹{metrics.potentialProfit.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{metrics.profitPercent.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Potential Loss</p>
                        <p className="text-lg font-bold text-red-600">₹{metrics.potentialLoss.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{metrics.lossPercent.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Risk/Reward Ratio</p>
                        <p className="text-lg font-bold text-blue-600">1:{metrics.riskRewardRatio.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Total Investment</p>
                        <p className="text-lg font-bold text-gray-800">
                          ₹{(parseFloat(formData.buy_limit_price) * parseInt(formData.quantity)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Important Notes */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-yellow-900 mb-2">Important Notes</h3>
                      <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                        <li><strong>GTT orders are valid for one year</strong> or until triggered/cancelled</li>
                        <li>Orders are for <strong>CNC (Equity Delivery)</strong> - suitable for long-term holding</li>
                        <li>GTT orders can be placed <strong>24/7</strong> (no market hours restriction)</li>
                        <li><strong>Buy Limit Price</strong> must be ≥ Buy Trigger Price</li>
                        <li><strong>Target Trigger</strong> must be &gt; Buy Limit Price</li>
                        <li><strong>Stop-Loss Trigger</strong> must be &lt; Buy Limit Price</li>
                        <li><strong>OCO:</strong> When either Target or Stop-Loss triggers, the other is automatically cancelled</li>
                        <li>Ensure <strong>sufficient funds</strong> in your trading account</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Risk Acknowledgment Checkbox */}
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                  <div className="flex items-start">
                    <input
                      id="disclaimer-ack"
                      type="checkbox"
                      checked={hasAcknowledged}
                      onChange={(e) => setHasAcknowledged(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1 flex-shrink-0"
                    />
                    <label htmlFor="disclaimer-ack" className="ml-3 block text-sm text-gray-900">
                      <strong>I understand that TrendVector is a data analysis tool, not an investment advisor.</strong> I acknowledge the risks of trading and I am solely responsible for this order and its outcome.
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !metrics.isValid || connectedBrokers.length === 0 || !hasAcknowledged}
                    className={`flex-1 px-6 py-3 rounded-md font-semibold text-white transition-colors ${
                      loading || !metrics.isValid || connectedBrokers.length === 0 || !hasAcknowledged
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700'
                    }`}
                    title={!hasAcknowledged ? 'Please acknowledge the risk disclaimer' : ''}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <FaSpinner className="animate-spin mr-2" />
                        Placing GTT Orders...
                      </span>
                    ) : (
                      'Place GTT Order'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTicketModal;
